import { useNavigate } from 'react-router-dom';
import { ActivityCardData } from './types';
import { LikeCountBadge } from '../activity-details';
import { ClockIcon, PeopleIcon, ChecklistIcon } from '../activity-details/icons';
import { createActivityNavigationHandler } from '../../utils/navigation';
import {
  CARD_DECK_COLORS,
  CARD_DECK_RADIUS,
  CARD_DECK_SHADOWS,
  CARD_DECK_SPACING,
  CARD_DECK_TYPOGRAPHY,
} from './constants';

interface ActivityDeckCardProps {
  card: ActivityCardData;
}

/**
 * ActivityDeckCard Component
 * 
 * Standard activity card for the card deck.
 * Displays activity image, title, metadata, description,
 * recommendation section, and CTA button.
 * 
 * Design specs from Figma:
 * - Width: 382px, Height: 462px (at full scale)
 * - Background: White, Border radius: 24px
 * - Padding: 16px, Gap: 10px
 * - Shadow: 0px 4px 16px rgba(0, 117, 141, 0.2)
 */
export default function ActivityDeckCard({ card }: ActivityDeckCardProps) {
  const navigate = useNavigate();
  const handleActivityClick = createActivityNavigationHandler(card.id, navigate);

  return (
    <div
      onClick={handleActivityClick}
      className="flex flex-col bg-white overflow-hidden w-full aspect-[382/462] p-4 sm:p-[18px] md:p-5 lg:p-[22px]"
      style={{
        borderRadius: `${CARD_DECK_RADIUS.card}px`,
        boxShadow: CARD_DECK_SHADOWS.card,
        gap: '10px',
      }}
    >
      {/* Activity Image with Like Badge */}
      <div
        className="relative overflow-hidden flex-shrink-0 aspect-[382/180]"
        style={{
          borderRadius: `${CARD_DECK_RADIUS.image}px`,
        }}
      >
        <img
          src={card.imageUrl}
          alt={card.title}
          className="w-full h-full object-cover"
        />
        {(card?.likesCount ?? 0) > 0 && (
          <LikeCountBadge
            count={card.likesCount}
            className="absolute bottom-2 right-2"
          />
        )}
      </div>

      {/* Title */}
      <h3
        className="text-[18px] sm:text-[19px] md:text-[20px] lg:text-[22px]"
        style={{
          fontFamily: CARD_DECK_TYPOGRAPHY.title.fontFamily,
          fontWeight: CARD_DECK_TYPOGRAPHY.title.fontWeight,
          lineHeight: CARD_DECK_TYPOGRAPHY.title.lineHeight,
          color: CARD_DECK_COLORS.titleText,
          margin: 0,
          fontVariationSettings: "'opsz' 14",
        }}
      >
        {card.title}
      </h3>

      {/* Metadata Row */}
      <div
        className="flex items-center"
        style={{ gap: `${CARD_DECK_SPACING.metadataGap}px` }}
      >
        {/* Duration */}
        <div className="flex items-center gap-1">
          <ClockIcon size={13} className="sm:w-[14px] sm:h-[14px] md:w-[15px] md:h-[15px]" color={CARD_DECK_COLORS.titleText} />
          <span
            className="text-[10px] sm:text-[11px] md:text-xs lg:text-[13px]"
            style={{
              fontFamily: CARD_DECK_TYPOGRAPHY.metadata.fontFamily,
              fontWeight: CARD_DECK_TYPOGRAPHY.metadata.fontWeight,
              lineHeight: CARD_DECK_TYPOGRAPHY.metadata.lineHeight,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.metadata.duration}
          </span>
        </div>

        {/* Play Type */}
        <div className="flex items-center gap-1">
          <PeopleIcon size={13} className="sm:w-[14px] sm:h-[14px] md:w-[15px] md:h-[15px]" color={CARD_DECK_COLORS.titleText} />
          <span
            className="text-[10px] sm:text-[11px] md:text-xs lg:text-[13px]"
            style={{
              fontFamily: CARD_DECK_TYPOGRAPHY.metadata.fontFamily,
              fontWeight: CARD_DECK_TYPOGRAPHY.metadata.fontWeight,
              lineHeight: CARD_DECK_TYPOGRAPHY.metadata.lineHeight,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.metadata.playType}
          </span>
        </div>

        {/* Prep Status */}
        <div className="flex items-center gap-1 flex-1">
          <ChecklistIcon size={13} className="sm:w-[14px] sm:h-[14px] md:w-[15px] md:h-[15px]" color={CARD_DECK_COLORS.titleText} />
          <span
            className="text-[10px] sm:text-[11px] md:text-xs lg:text-[13px]"
            style={{
              fontFamily: CARD_DECK_TYPOGRAPHY.metadata.fontFamily,
              fontWeight: CARD_DECK_TYPOGRAPHY.metadata.fontWeight,
              lineHeight: CARD_DECK_TYPOGRAPHY.metadata.lineHeight,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.metadata.prepNeeded}
          </span>
        </div>
      </div>

      {/* Description */}
      <p
        className="line-clamp-2 flex-shrink-0 text-[13px] sm:text-[14px] md:text-[15px] lg:text-[17px]"
        style={{
          fontFamily: CARD_DECK_TYPOGRAPHY.description.fontFamily,
          fontWeight: CARD_DECK_TYPOGRAPHY.description.fontWeight,
          lineHeight: CARD_DECK_TYPOGRAPHY.description.lineHeight,
          color: CARD_DECK_COLORS.descriptionText,
          margin: 0,
          fontVariationSettings: "'opsz' 14",
        }}
      >
        {card.description}
      </p>

      {/* Why it works chip */}
      {card.whyItWorks && (
        <div className="flex-shrink-0 mt-2" style={{
          backgroundColor: '#DBF7F8',
          borderRadius: '8px',
          padding: '8px 12px'
        }}>
          <p
            className="line-clamp-2 text-[11px] sm:text-[12px] md:text-[13px]"
            style={{
              fontFamily: CARD_DECK_TYPOGRAPHY.description.fontFamily,
              color: '#007CAD',
              margin: 0,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.whyItWorks.text}
          </p>
        </div>
      )}

      {/* Recommendation Section — only shown for engine recommendations */}
      {card.recommendation && (
        <div className="py-2">
          <div className="flex items-center gap-2">
            {/* Child Avatar with Badge */}
            <div className="relative">
              {card.recommendation.childAvatarUrl ? (
                <img
                  src={card.recommendation.childAvatarUrl}
                  alt={`${card.recommendation.childName}'s avatar`}
                  className="rounded-full object-cover"
                  style={{ width: '32px', height: '32px' }}
                />
              ) : (
                <div
                  className="rounded-full flex items-center justify-center"
                  style={{ width: '32px', height: '32px', backgroundColor: '#DBF7F8' }}
                />
              )}
              {/* Spark Badge */}
              <div
                className="absolute flex items-center justify-center bg-white"
                style={{
                  width: '12px',
                  height: '12px',
                  top: '-0.5px',
                  right: '-0.5px',
                  borderRadius: '8px',
                  border: '1px solid white',
                }}
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <path
                    d="M4 0L4.89 2.76L7.8 3.09L5.62 5.01L6.24 7.91L4 6.5L1.76 7.91L2.38 5.01L0.2 3.09L3.11 2.76L4 0Z"
                    fill={CARD_DECK_COLORS.skillText}
                  />
                </svg>
              </div>
            </div>

            {/* Recommendation Text */}
            <div className="flex flex-col">
              <span
                className="text-[11px] sm:text-xs md:text-[13px] lg:text-[14px]"
                style={{
                  fontFamily: CARD_DECK_TYPOGRAPHY.recommendationTitle.fontFamily,
                  fontWeight: CARD_DECK_TYPOGRAPHY.recommendationTitle.fontWeight,
                  color: CARD_DECK_COLORS.titleText,
                  fontVariationSettings: "'opsz' 14",
                }}
              >
                Recommended for {card.recommendation.childName}
              </span>
              <div className="flex items-center gap-1">
                <span
                  className="text-[10px] sm:text-[11px] md:text-xs lg:text-[13px]"
                  style={{
                    fontFamily: CARD_DECK_TYPOGRAPHY.metadata.fontFamily,
                    fontWeight: CARD_DECK_TYPOGRAPHY.metadata.fontWeight,
                    lineHeight: CARD_DECK_TYPOGRAPHY.metadata.lineHeight,
                    color: CARD_DECK_COLORS.strengthensText,
                    fontVariationSettings: "'opsz' 14",
                  }}
                >
                  Strengthens
                  <span
                    className="ml-1 text-[10px] sm:text-[11px] md:text-xs lg:text-[13px]"
                    style={{
                      fontFamily: CARD_DECK_TYPOGRAPHY.metadata.fontFamily,
                      fontWeight: 700,
                      lineHeight: CARD_DECK_TYPOGRAPHY.metadata.lineHeight,
                      color: card.categoryColor || CARD_DECK_COLORS.skillText,
                      fontVariationSettings: "'opsz' 14",
                    }}
                  >
                    {card.recommendation.skillCategory}
                  </span>
                  <span
                    className="ml-1 text-[10px] sm:text-[11px] md:text-xs lg:text-[13px]"
                    style={{
                      fontFamily: CARD_DECK_TYPOGRAPHY.metadata.fontFamily,
                      fontWeight: CARD_DECK_TYPOGRAPHY.metadata.fontWeight,
                      lineHeight: CARD_DECK_TYPOGRAPHY.metadata.lineHeight,
                      color: CARD_DECK_COLORS.strengthensText,
                      fontVariationSettings: "'opsz' 14",
                    }}
                  >
                    Skills
                  </span>
                </span>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={(e) => { e.stopPropagation(); handleActivityClick(); }}
        className="w-full flex items-center justify-center h-12 sm:h-[50px] md:h-[52px] lg:h-14 text-[13px] sm:text-[14px] md:text-[15px] lg:text-base"
        style={{
          backgroundColor: card.categoryColor || CARD_DECK_COLORS.buttonBackground,
          borderRadius: `${CARD_DECK_RADIUS.button}px`,
          border: 'none',
          cursor: 'pointer',
          fontFamily: CARD_DECK_TYPOGRAPHY.button.fontFamily,
          fontWeight: CARD_DECK_TYPOGRAPHY.button.fontWeight,
          letterSpacing: CARD_DECK_TYPOGRAPHY.button.letterSpacing,
          textTransform: CARD_DECK_TYPOGRAPHY.button.textTransform,
          color: CARD_DECK_COLORS.buttonText,
          fontVariationSettings: "'opsz' 14",
        }}
      >
        TRY THIS SPARK
      </button>
    </div>
  );
}


